import { useState, useMemo } from 'react';
import { MoreVertical, Folder, Target, CheckCircle, Calendar, ChevronDown, ChevronUp, FolderOpen, User, Check } from 'lucide-react';
import { Matter, Goal, Task, Project, Client } from '../../lib/db';

interface MatterCardMobileProps {
  matter: Matter;
  goals: Goal[];
  tasks?: Task[];
  projects?: Project[];
  clients?: Client[];
  onEdit: (matter: Matter) => void;
  onDelete: (matter: Matter) => void;
  onMarkComplete?: (matter: Matter) => void;
  onMarkIncomplete?: (matter: Matter) => void;
}

export function MatterCardMobile({
  matter,
  goals,
  tasks,
  projects,
  clients,
  onEdit,
  onDelete,
  onMarkComplete,
  onMarkIncomplete,
}: MatterCardMobileProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const associatedGoals = goals.filter(goal => goal.matter_id === matter.id);
  const goalCount = associatedGoals.length;
  
  const statistics = useMemo(() => {
    if (!tasks) return { totalTasks: 0, completedTasks: 0, allDeliverablesComplete: false };
    
    const goalIds = associatedGoals.map(g => g.id);
    const matterTasks = tasks.filter(task => task.goal_id && goalIds.includes(task.goal_id));
    const completedTasks = matterTasks.filter(task => task.status === 'Complete').length;
    
    // Check if all deliverables are complete (have all their tasks completed)
    const allDeliverablesComplete = associatedGoals.length > 0 && associatedGoals.every(goal => {
      const goalTasks = tasks.filter(task => task.goal_id === goal.id);
      return goalTasks.length > 0 && goalTasks.every(task => task.status === 'Complete');
    });
    
    return {
      totalTasks: matterTasks.length,
      completedTasks,
      allDeliverablesComplete,
    };
  }, [tasks, associatedGoals]);

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete "${matter.title}"?`)) {
      onDelete(matter);
    }
    setShowMenu(false);
  };

  const getStatusColor = () => {
    switch (matter.status) {
      case 'Active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Completed':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Archived':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (date: Date | undefined): string => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric'
    });
  };

  const completionPercentage = statistics.totalTasks > 0 
    ? Math.round((statistics.completedTasks / statistics.totalTasks) * 100)
    : 0;

  const associatedProject = matter.project_id && projects 
    ? projects.find(p => p.id === matter.project_id)
    : null;
  
  const associatedClient = matter.client_id && clients
    ? clients.find(c => c.id === matter.client_id)
    : null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center flex-1 min-w-0">
            <Folder className="h-5 w-5 text-gray-400 flex-shrink-0 mr-2" />
            <h3 className="text-base font-medium text-gray-900 truncate">
              {matter.title}
            </h3>
            {matter.is_complete && (
              <CheckCircle className="h-4 w-4 text-green-500 ml-1 flex-shrink-0" />
            )}
          </div>
          <div className="relative ml-2">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1 rounded-full hover:bg-gray-100"
            >
              <MoreVertical className="h-5 w-5 text-gray-400" />
            </button>
            {showMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowMenu(false)}
                />
                <div className="absolute right-0 top-8 z-20 w-48 bg-white rounded-md shadow-lg border border-gray-200">
                  <button
                    onClick={() => {
                      onEdit(matter);
                      setShowMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Edit matter
                  </button>
                  {statistics.allDeliverablesComplete && !matter.is_complete && onMarkComplete && (
                    <button
                      onClick={() => {
                        onMarkComplete(matter);
                        setShowMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-green-700 hover:bg-gray-100"
                    >
                      Mark Complete
                    </button>
                  )}
                  {matter.is_complete && onMarkIncomplete && (
                    <button
                      onClick={() => {
                        onMarkIncomplete(matter);
                        setShowMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Mark Incomplete
                    </button>
                  )}
                  <button
                    onClick={handleDelete}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                  >
                    Delete matter
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Status Badge, Project and Client */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor()}`}>
            {matter.status}
          </span>
          {associatedProject && (
            <div className="flex items-center text-xs text-purple-600">
              <FolderOpen className="h-3.5 w-3.5 mr-1" />
              <span className="truncate">{associatedProject.title}</span>
            </div>
          )}
          {associatedClient && (
            <div className="flex items-center text-xs text-indigo-600">
              <User className="h-3.5 w-3.5 mr-1" />
              <span className="truncate">{associatedClient.name}</span>
            </div>
          )}
        </div>

        {/* Description */}
        {matter.description && (
          <p className="text-sm text-gray-600 mb-3 line-clamp-2">
            {matter.description}
          </p>
        )}

        {/* Stats */}
        <div className="flex items-center gap-4 mb-3 text-sm text-gray-600">
          <div className="flex items-center">
            <Target className="h-4 w-4 mr-1.5 text-gray-400" />
            <span>{goalCount} deliverable{goalCount !== 1 ? 's' : ''}</span>
          </div>
          {statistics.totalTasks > 0 && (
            <div className="flex items-center">
              <CheckCircle className="h-4 w-4 mr-1.5 text-gray-400" />
              <span>{statistics.completedTasks}/{statistics.totalTasks}</span>
            </div>
          )}
        </div>

        {/* Dates */}
        {(matter.start_date || matter.deadline) && (
          <div className="flex flex-wrap gap-2 mb-3 text-xs">
            {matter.start_date && (
              <div className="flex items-center text-green-600">
                <Calendar className="h-3.5 w-3.5 mr-1" />
                <span>Start: {formatDate(matter.start_date)}</span>
              </div>
            )}
            {matter.deadline && (
              <div className="flex items-center text-red-600">
                <Calendar className="h-3.5 w-3.5 mr-1" />
                <span>Due: {formatDate(matter.deadline)}</span>
              </div>
            )}
          </div>
        )}

        {/* Progress Bar */}
        {statistics.totalTasks > 0 && (
          <div>
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs text-gray-600">Progress</span>
              <span className="text-xs font-medium text-gray-900">{completionPercentage}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${completionPercentage}%` }}
              />
            </div>
          </div>
        )}

        {/* Expand/Collapse Button */}
        {goalCount > 0 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-3 flex items-center justify-center w-full py-1 text-sm text-gray-600 hover:text-gray-900"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="h-4 w-4 mr-1" />
                Hide deliverables
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4 mr-1" />
                Show deliverables
              </>
            )}
          </button>
        )}
      </div>

      {/* Expanded Content */}
      {isExpanded && goalCount > 0 && (
        <div className="border-t border-gray-200 px-4 py-3 bg-gray-50">
          <h4 className="text-xs font-medium text-gray-700 mb-1">Associated Deliverables</h4>
          <div className="space-y-1">
            {associatedGoals.map(goal => (
              <div key={goal.id} className="text-sm text-gray-600 pl-3">
                • {goal.title}
                {goal.category && (
                  <span className="ml-1 text-xs text-gray-500">({goal.category})</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}